import { useEffect, useMemo, useState } from "react";
import { DataVisualizationType } from "components/charting/config/chartTypes";
import ChartSetSelectContext from "components/charting/contexts/ChartSetSelectContext";
import ChartSetSelectStatusContext from "components/charting/contexts/ChartSetSelectStatusContext";
import useChartSelections from "components/charting/hooks/useChartSelections";
import useDataVisualizationType from "components/charting/hooks/useDataVisualizationType";
import useGetMeasurementSetsQuery from "hooks/useGetMeasurementSetsQuery";
import type { MeasurementSet } from "types";
import AnalyticsService from "services/AnalyticsService";
import logEvent from "utils/logEvent";
import ArrayHelpers from "helpers/ArrayHelpers";

interface ChartSetSelectProviderProps {
  children?: React.ReactNode;
}

function ChartSetSelectProvider(props: ChartSetSelectProviderProps) {
  const selections = useChartSelections();
  const visType = useDataVisualizationType();
  const [selectedSets, setSelectedSets] = useState<MeasurementSet[]>([]);
  const measure = selections.measures[0];
  const athleteId = selections.sources[0]?.id;
  const exerciseId = measure.id;

  const enableSetsApi = Boolean(
    [DataVisualizationType.SET_COMPARISON_CHART, DataVisualizationType.PROFILING_CHART].includes(visType) &&
    measure.type === "Exercise" &&
    measure.metadata.repetitions.length
  );

  const { data: measurementSetsData, status: measurementSetsStatus } = useGetMeasurementSetsQuery(
    { athleteId, exerciseId },
    { enabled: enableSetsApi }
  );

  const exerciseCategory = measure.type === "Exercise" ? measure.metadata.categoryId : undefined;

  useEffect(() => {
    /**
     * Event Log pattern copied from when
     * @function useGetMeasurementSetsQuery makes an api call
     **/

    if (!(enableSetsApi && exerciseId && athleteId)) return;

    logEvent("ATHLETE_PROFILE_VIEW_DATA", {
      [AnalyticsService.PROPERTIES.CHART_TYPE]: visType,
      [AnalyticsService.PROPERTIES.VISUALIZATION_TYPE]: visType,
      [AnalyticsService.PROPERTIES.CHARTING_MEASURE_ID]: exerciseId,
      [AnalyticsService.PROPERTIES.EXERCISE_ID]: measure.type === "Exercise" ? exerciseId : undefined,
      [AnalyticsService.PROPERTIES.CHARTING_MEASURE_TYPE]: measure.type,
      [AnalyticsService.PROPERTIES.EXERCISE_CATEGORY]: exerciseCategory,
    });
  }, [athleteId, enableSetsApi, exerciseId, exerciseCategory, measure.type, visType]);

  // Drop selected sets once they stop being offered, so they don't return when the exercise or athlete is switched back.
  useEffect(() => {
    setSelectedSets((sets) => ArrayHelpers.reconcile(sets, measurementSetsData, (set) => set.measurementId));
  }, [measurementSetsData]);

  const contextValue = useMemo(
    () => ({
      value: ArrayHelpers.reconcile(selectedSets, measurementSetsData, (set) => set.measurementId),
      options: measurementSetsData,
      onChange: (sets: MeasurementSet[]) => {
        setSelectedSets(sets);
      },
    }),
    [measurementSetsData, selectedSets]
  );

  return (
    <ChartSetSelectStatusContext.Provider value={measurementSetsStatus}>
      <ChartSetSelectContext.Provider value={contextValue}>{props.children}</ChartSetSelectContext.Provider>
    </ChartSetSelectStatusContext.Provider>
  );
}

export default ChartSetSelectProvider;
